/**
 * @voltxampere/spice — Current source codec (I)
 *
 * Berkeley SPICE3 syntax: `I{name} N+ N- value [DC value | AC mag phase]`
 *
 * Examples:
 *   I1 0 net1 1m
 *   Iload load 0 DC 100u
 *
 * Sprint 16 only handles the DC variant (no AC keyword, no PULSE
 * — Sprint 19+ adds those if a use case lands).
 *
 * Schema mapping: `type: "current-source"`, params: `{ current }`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ok, err, } from "./_base.js";
import { parseSpiceNumber, formatSpiceValue } from "../units.js";
import { numericParam } from "./_param-helpers.js";
export const currentSourceCodec = {
    spiceLetter: "I",
    schemaTypes: ["current-source"],
    examples: ["I1 0 net1 1m", "Iload load 0 DC 100u"],
    parse(card, _ctx) {
        if (card.tokens.length < 3) {
            return err("I needs 2 nodes and a value");
        }
        const [na, nb, ...rest] = card.tokens;
        if (na === undefined || nb === undefined) {
            return err("I missing nodes");
        }
        let valueRaw;
        if (rest[0]?.toUpperCase() === "DC")
            valueRaw = rest[1];
        else
            valueRaw = rest[0];
        if (valueRaw === undefined)
            return err("I missing value");
        const value = parseSpiceNumber(valueRaw);
        if (value === null)
            return err(`I value '${valueRaw}' is not a valid number`);
        const component = {
            id: card.identifier,
            type: "current-source",
            position: { x: 0, y: 0 },
            rotation: 0,
            flipped: false,
            label: card.identifier,
            params: { current: value },
            terminals: [],
        };
        return ok(component, [na, nb]);
    },
    emit(component, terminals) {
        const i = numericParam(component.params.current, 1e-3);
        const [a, b] = terminals;
        return `${component.id} ${a ?? "0"} ${b ?? "0"} ${formatSpiceValue(i)}`;
    },
    owns(schemaType) {
        return schemaType === "current-source";
    },
};
//# sourceMappingURL=current-source.js.map